import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  FileType,
  Image,
  Video,
  Music,
  FileSpreadsheet,
  Archive,
  File,
  Code,
  Plus,
  X,
} from "lucide-react";
import { API_ENDPOINTS } from "../../../constants/constants";
import { Button } from "../../../components/ui/button";

// Interfaces
interface Resource {
  id: number;
  title: string;
  description: string;
  type: string;
  image: string;
  badge?: string;
  moduleId?: string;
  moduleTitle?: string;
  fileUrl?: string;
  filename?: string; // Nom du fichier avec extension
  mimetype?: string; // Type MIME du fichier
}

interface Module {
  id: string;
  title: string;
  bgColor: string;
  resources: Resource[];
}

// Interface pour le module récupéré de l'API
interface ModuleData {
  id: string;
  title: string;
  order?: number;
  description?: string;
  content?: unknown; // Propriété de contenu de type inconnu
  createdAt?: string;
  updatedAt?: string;
}

const RessourcesPedagogiques: React.FC = () => {
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    moduleId: "",
  });

  // Fonction pour récupérer les ressources
  const fetchResources = async () => {
    setLoading(true);
    setError(null);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Récupérer tous les modules pour avoir leurs titres
      const modulesResponse = await fetch(
        `${API_ENDPOINTS.CINEPRO.GET_MODULES}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!modulesResponse.ok) {
        console.warn("Impossible de récupérer les modules");
      }

      // Créer un mapping des modules pour récupérer leurs titres
      const modulesData = await modulesResponse.json();
      const moduleIdToTitle = new Map();

      if (Array.isArray(modulesData)) {
        modulesData.forEach((module: ModuleData) => {
          if (module.id && module.title) {
            moduleIdToTitle.set(module.id, module.title);
          }
        });
      }

      // Récupérer toutes les ressources
      const response = await fetch(`${API_ENDPOINTS.RESOURCES.GET_RESOURCES}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Organiser les ressources par module
      const moduleMap = new Map<string, Module>();

      // Définition des couleurs d'arrière-plan par défaut pour les modules
      const bgColors = ["bg-[#fff8eb]", "bg-[#f8fafc]"];

      // Regrouper les ressources par moduleId
      data.forEach((resource: Resource) => {
        const moduleId = resource.moduleId || "default";

        if (!moduleMap.has(moduleId)) {
          // Récupérer le titre du module depuis le mapping
          const moduleTitle = moduleIdToTitle.get(moduleId);
          const moduleIndex = moduleMap.size + 1;

          // Créer un nouveau module s'il n'existe pas déjà
          moduleMap.set(moduleId, {
            id: moduleId,
            title: `Module ${moduleIndex} : ${moduleTitle || "Sans titre"}`,
            bgColor: bgColors[moduleMap.size % bgColors.length],
            resources: [],
          });
        }

        // Ajouter la ressource au module correspondant
        const module = moduleMap.get(moduleId);
        if (module) {
          module.resources.push(resource);
        }
      });

      // Convertir la Map en tableau
      setModules(Array.from(moduleMap.values()));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue");
      console.error("Erreur lors de la récupération des ressources:", err);
    } finally {
      setLoading(false);
    }
  };

  // Récupération des ressources depuis l'API
  useEffect(() => {
    fetchResources();
  }, []);

  // Fonction pour télécharger une ressource
  const handleDownloadResource = async (resourceId: number) => {
    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Effectuer la requête fetch avec l'en-tête Authorization
      const response = await fetch(
        API_ENDPOINTS.RESOURCES.DOWNLOAD_RESOURCE(resourceId.toString()),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Erreur lors du téléchargement du fichier");
      }

      // Récupérer le blob de la réponse
      const blob = await response.blob();

      // Créer une URL d'objet pour le blob
      const url = window.URL.createObjectURL(blob);

      // Créer un élément <a> temporaire pour déclencher le téléchargement
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;

      // Essayer d'obtenir le nom du fichier depuis les en-têtes de réponse
      const contentDisposition = response.headers.get("content-disposition");
      let filename = `resource-${resourceId}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]*)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      a.download = filename;

      // Ajouter l'élément au DOM, cliquer dessus, puis le supprimer
      document.body.appendChild(a);
      a.click();

      // Nettoyer
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Erreur lors du téléchargement:", err);
      alert("Erreur lors du téléchargement de la ressource");
    }
  };

  // Fonction pour faire fonctionner les carrousels
  const scrollCarousel = (moduleId: string, direction: "left" | "right") => {
    const container = document.getElementById(`carousel-${moduleId}`);
    if (container) {
      const scrollAmount = container.clientWidth * 0.8;
      if (direction === "left") {
        container.scrollBy({ left: -scrollAmount, behavior: "smooth" });
      } else {
        container.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
    }
  };

  // Fonction pour ajouter une ressource
  const handleAddResource = () => {
    setIsModalOpen(true);
  };

  // Fonction pour gérer la sélection de fichier
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    setSelectedFile(file);

    // Créer une URL de prévisualisation pour les images
    if (file) {
      if (file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }
    } else {
      setPreviewUrl(null);
    }
  };

  // Nettoyer l'URL de prévisualisation lors de la fermeture du modal
  const handleCloseModal = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setIsModalOpen(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    setFormData({
      title: "",
      description: "",
      moduleId: "",
    });
  };

  // Fonction pour gérer la soumission du formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      if (!selectedFile) {
        throw new Error("Veuillez sélectionner un fichier");
      }

      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("moduleId", formData.moduleId);
      formDataToSend.append("file", selectedFile);

      const response = await fetch(API_ENDPOINTS.RESOURCES.CREATE_RESOURCE, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error("Erreur lors de la création de la ressource");
      }

      // Fermer le modal et réinitialiser le formulaire
      handleCloseModal();

      // Rafraîchir la liste des ressources
      fetchResources();
    } catch (err) {
      console.error("Erreur lors de la création de la ressource:", err);
      alert(err instanceof Error ? err.message : "Une erreur est survenue");
    }
  };

  // Composant pour afficher une ressource
  const ResourceCard = ({ resource }: { resource: Resource }) => {
    // Fonction pour déterminer l'icône à afficher en fonction du type de document
    const getResourceIcon = () => {
      // Vérifier si une image existe
      if (resource.image && resource.image !== "") {
        return (
          <img
            src={resource.image}
            alt={resource.title}
            className="w-full h-full object-cover"
          />
        );
      }

      // Définir une icône par défaut basée sur le type de ressource
      let IconComponent = File; // Icône par défaut
      let iconColor = "#6B7280"; // Couleur grise par défaut

      // Détection basée sur le mimetype, le filename, et en dernier recours le type
      const detectDocumentType = () => {
        // Vérifier d'abord le mimetype qui est le plus fiable
        if (resource.mimetype) {
          const mimetype = resource.mimetype.toLowerCase();

          if (mimetype === "application/pdf") {
            return "pdf";
          } else if (
            mimetype === "application/msword" ||
            mimetype ===
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          ) {
            return "word";
          } else if (
            mimetype === "application/vnd.ms-excel" ||
            mimetype ===
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
            mimetype === "text/csv"
          ) {
            return "excel";
          } else if (
            mimetype === "application/vnd.ms-powerpoint" ||
            mimetype ===
              "application/vnd.openxmlformats-officedocument.presentationml.presentation"
          ) {
            return "powerpoint";
          } else if (mimetype.startsWith("image/")) {
            return "image";
          } else if (mimetype.startsWith("video/")) {
            return "video";
          } else if (mimetype.startsWith("audio/")) {
            return "audio";
          } else if (
            mimetype === "application/zip" ||
            mimetype === "application/x-zip-compressed" ||
            mimetype === "application/x-rar-compressed"
          ) {
            return "archive";
          } else if (
            mimetype === "text/html" ||
            mimetype === "application/javascript" ||
            mimetype === "text/css"
          ) {
            return "code";
          }
        }

        // Vérifier ensuite le filename qui est assez fiable
        if (resource.filename) {
          const fileName = resource.filename.toLowerCase();

          if (fileName.endsWith(".pdf")) {
            return "pdf";
          } else if (fileName.endsWith(".doc") || fileName.endsWith(".docx")) {
            return "word";
          } else if (
            fileName.endsWith(".xls") ||
            fileName.endsWith(".xlsx") ||
            fileName.endsWith(".csv")
          ) {
            return "excel";
          } else if (fileName.endsWith(".ppt") || fileName.endsWith(".pptx")) {
            return "powerpoint";
          } else if (
            fileName.endsWith(".jpg") ||
            fileName.endsWith(".jpeg") ||
            fileName.endsWith(".png") ||
            fileName.endsWith(".gif")
          ) {
            return "image";
          } else if (
            fileName.endsWith(".mp4") ||
            fileName.endsWith(".mov") ||
            fileName.endsWith(".avi")
          ) {
            return "video";
          } else if (
            fileName.endsWith(".mp3") ||
            fileName.endsWith(".wav") ||
            fileName.endsWith(".ogg")
          ) {
            return "audio";
          } else if (fileName.endsWith(".zip") || fileName.endsWith(".rar")) {
            return "archive";
          } else if (
            fileName.endsWith(".html") ||
            fileName.endsWith(".js") ||
            fileName.endsWith(".css")
          ) {
            return "code";
          }
        }

        // En dernier recours, essayer le champ type
        if (resource.type) {
          const type = resource.type.toLowerCase();

          if (type.includes("pdf")) {
            return "pdf";
          } else if (type.includes("word") || type.includes("doc")) {
            return "word";
          } else if (
            type.includes("excel") ||
            type.includes("xls") ||
            type.includes("csv")
          ) {
            return "excel";
          } else if (type.includes("powerpoint") || type.includes("ppt")) {
            return "powerpoint";
          } else if (
            type.includes("image") ||
            type.includes("jpg") ||
            type.includes("jpeg") ||
            type.includes("png") ||
            type.includes("gif")
          ) {
            return "image";
          } else if (type.includes("video") || type.includes("mp4")) {
            return "video";
          } else if (
            type.includes("audio") ||
            type.includes("mp3") ||
            type.includes("wav")
          ) {
            return "audio";
          } else if (type.includes("zip") || type.includes("rar")) {
            return "archive";
          } else if (
            type.includes("code") ||
            type.includes("html") ||
            type.includes("js") ||
            type.includes("css")
          ) {
            return "code";
          }
        }

        return null; // Aucun type reconnu
      };

      // Déterminer le type de document
      const documentType = detectDocumentType();

      // Définir l'icône en fonction du type détecté
      if (documentType === "pdf") {
        IconComponent = FileType;
        iconColor = "#EC4899"; // Rose
      } else if (documentType === "word") {
        IconComponent = FileText;
        iconColor = "#3B82F6"; // Bleu
      } else if (documentType === "excel") {
        IconComponent = FileSpreadsheet;
        iconColor = "#10B981"; // Vert
      } else if (documentType === "powerpoint") {
        IconComponent = FileText;
        iconColor = "#F97316"; // Orange
      } else if (documentType === "image") {
        IconComponent = Image;
        iconColor = "#8B5CF6"; // Violet
      } else if (documentType === "video") {
        IconComponent = Video;
        iconColor = "#EF4444"; // Rouge
      } else if (documentType === "audio") {
        IconComponent = Music;
        iconColor = "#F59E0B"; // Jaune
      } else if (documentType === "archive") {
        IconComponent = Archive;
        iconColor = "#6B7280"; // Gris
      } else if (documentType === "code") {
        IconComponent = Code;
        iconColor = "#0EA5E9"; // Bleu ciel
      }

      // Retourner l'icône avec la couleur appropriée
      return (
        <div className="flex items-center justify-center w-full h-full bg-gray-50">
          <div className="flex flex-col items-center justify-center">
            <IconComponent size={64} color={iconColor} strokeWidth={1.5} />
            <div className="mt-2 text-sm font-medium text-gray-600">
              {resource.type || "Document"}
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="min-w-[250px] max-w-[250px] bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm flex flex-col">
        <div className="h-36 bg-gray-50 relative flex items-center justify-center">
          {resource.type && (
            <div className="absolute top-2 left-2 bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
              {resource.type}
            </div>
          )}
          {resource.badge && (
            <div className="absolute text-2xl font-bold text-white bg-red-600 px-3 py-1 transform rotate-0 z-10">
              {resource.badge}
            </div>
          )}
          {getResourceIcon()}
        </div>
        <div className="p-4 flex flex-col flex-grow">
          <h3 className="font-semibold text-gray-800 mb-2 text-center">
            {resource.title}
          </h3>
          <p className="text-xs text-gray-500 mb-3 text-center">
            {resource.description}
          </p>
          <button
            onClick={() => handleDownloadResource(resource.id)}
            className="w-full py-1.5 text-white bg-orange-500 hover:bg-orange-600 rounded text-sm flex items-center justify-center"
          >
            <span>Télécharger</span>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold mb-8 text-gray-800">
            Ressources Pédagogiques
          </h1>
          <Button
            onClick={handleAddResource}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md shadow-sm transition-colors duration-200 flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            <span className="hidden sm:inline">Ajouter une ressource</span>
            <span className="sm:hidden">Ajouter</span>
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            <p className="font-medium">Erreur: {error}</p>
          </div>
        ) : modules.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 text-gray-700 px-4 py-8 rounded-md text-center">
            <p className="text-lg">
              Aucune ressource pédagogique n'est disponible pour le moment.
            </p>
            <Button
              onClick={handleAddResource}
              className="mt-4 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md shadow-sm transition-colors duration-200"
            >
              Ajouter votre première ressource
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {modules.map((module) => (
              <div
                key={module.id}
                className={`${module.bgColor} p-6 rounded-lg shadow-sm`}
              >
                <h2 className="text-xl font-semibold mb-4 text-gray-800">
                  {module.title}
                </h2>
                <div className="relative">
                  <button
                    onClick={() => scrollCarousel(module.id, "left")}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-md hover:bg-gray-100 transition-colors duration-200"
                    aria-label="Défiler vers la gauche"
                  >
                    <ChevronLeft className="h-6 w-6 text-gray-600" />
                  </button>
                  <div
                    id={`carousel-${module.id}`}
                    className="flex gap-4 overflow-x-auto pb-4 px-4 scrollbar-hide"
                    style={{ scrollbarWidth: "none" }}
                  >
                    {module.resources.map((resource) => (
                      <ResourceCard key={resource.id} resource={resource} />
                    ))}
                  </div>
                  <button
                    onClick={() => scrollCarousel(module.id, "right")}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-md hover:bg-gray-100 transition-colors duration-200"
                    aria-label="Défiler vers la droite"
                  >
                    <ChevronRight className="h-6 w-6 text-gray-600" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de création de ressource */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">
                Ajouter une ressource
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                aria-label="Fermer"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titre
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  rows={3}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Module
                </label>
                <select
                  value={formData.moduleId}
                  onChange={(e) =>
                    setFormData({ ...formData, moduleId: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                >
                  <option value="">Sélectionner un module</option>
                  {modules.map((module) => (
                    <option key={module.id} value={module.id}>
                      {module.title}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fichier
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-green-500 transition-colors duration-200">
                  <div className="space-y-1 text-center">
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="Aperçu"
                        className="mx-auto h-32 w-auto object-contain"
                      />
                    ) : selectedFile ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-center">
                          {selectedFile.type.startsWith("image/") ? (
                            <Image className="h-12 w-12 text-green-500" />
                          ) : selectedFile.type.includes("pdf") ? (
                            <FileType className="h-12 w-12 text-red-500" />
                          ) : selectedFile.type.includes("word") ||
                            selectedFile.type.includes("document") ? (
                            <FileText className="h-12 w-12 text-blue-500" />
                          ) : selectedFile.type.includes("excel") ||
                            selectedFile.type.includes("spreadsheet") ? (
                            <FileSpreadsheet className="h-12 w-12 text-green-600" />
                          ) : selectedFile.type.includes("powerpoint") ||
                            selectedFile.type.includes("presentation") ? (
                            <FileText className="h-12 w-12 text-orange-500" />
                          ) : selectedFile.type.includes("video") ? (
                            <Video className="h-12 w-12 text-red-500" />
                          ) : selectedFile.type.includes("audio") ? (
                            <Music className="h-12 w-12 text-yellow-500" />
                          ) : selectedFile.type.includes("zip") ||
                            selectedFile.type.includes("rar") ? (
                            <Archive className="h-12 w-12 text-gray-500" />
                          ) : selectedFile.type.includes("html") ||
                            selectedFile.type.includes("javascript") ||
                            selectedFile.type.includes("css") ? (
                            <Code className="h-12 w-12 text-blue-400" />
                          ) : (
                            <File className="h-12 w-12 text-gray-500" />
                          )}
                        </div>
                        <p className="text-sm font-medium text-gray-700 truncate max-w-xs mx-auto">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedFile(null);
                            setPreviewUrl(null);
                          }}
                          className="text-xs text-red-500 hover:text-red-700 transition-colors duration-200"
                        >
                          Supprimer
                        </button>
                      </div>
                    ) : (
                      <>
                        <File className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="flex text-sm text-gray-600">
                          <label
                            htmlFor="file-upload"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-green-600 hover:text-green-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-500"
                          >
                            <span>Télécharger un fichier</span>
                            <input
                              id="file-upload"
                              name="file-upload"
                              type="file"
                              className="sr-only"
                              onChange={handleFileSelect}
                              required
                            />
                          </label>
                          <p className="pl-1">ou glisser-déposer</p>
                        </div>
                        <p className="text-xs text-gray-500">
                          PDF, WORD, EXCEL, POWERPOINT jusqu'à 10MB
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  onClick={handleCloseModal}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800 transition-colors duration-200"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700 text-white transition-colors duration-200"
                >
                  Créer
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RessourcesPedagogiques;
